import { DOMUtil, Util, Vector2, Viewport } from '../rocket';

export type PointerMoveEventType = 'move' | 'enter' | 'leave';

export interface PointerMoveEvent {
  event: MouseEvent;
  acceleration: Vector2;
  velocity: Vector2;
  position: Vector2;
  time: number;
  type: PointerMoveEventType;
}

export interface PointerMoveEventManagerConfig {
  target?: HTMLElement | Window;
  debounceWait: number;
  onEvent: (mouseEvent: MouseEvent, manager: PointerMoveEventManager) => void;
  onEnter: (pointerEvent: PointerMoveEvent, manager: PointerMoveEventManager) => void;
  onLeave: (pointerEvent: PointerMoveEvent, manager: PointerMoveEventManager) => void;
  onMoveStart: (pointerEvent: PointerMoveEvent, manager: PointerMoveEventManager) => void;
  onMove: (pointerEvent: PointerMoveEvent, manager: PointerMoveEventManager) => void;
  onMoveEnd: (pointerEvent: PointerMoveEvent, manager: PointerMoveEventManager) => void;
  onMoveInside: (pointerEvent: PointerMoveEvent, manager: PointerMoveEventManager) => void;
}

export const POINTER_MOVE_EVENT_MANAGER_DEFAULT_CONFIG: PointerMoveEventManagerConfig = {
  target: window,
  debounceWait: 200,
  onEvent: () => {},
  onMoveStart: () => {},
  onMove: () => {},
  onMoveInside: () => {},
  onMoveEnd: () => {},
  onEnter: () => {},
  onLeave: () => {},
};

export class PointerMoveEventManager {
  public config: PointerMoveEventManagerConfig;

  public isInside: boolean = false;
  public isMoving: boolean = false;
  private debouncer?: Function;

  private position: Vector2;
  private velocity: Vector2;
  private acceleration: Vector2;
  private previousPosition: Vector2;
  private previousVelocity: Vector2;

  public previousPointerMoveStartEvent?: PointerMoveEvent;
  public previousPointerEvent?: PointerMoveEvent;
  public previousPointerMoveEvent?: PointerMoveEvent;

  constructor(config?: Partial<PointerMoveEventManagerConfig>) {
    this.config = Object.assign({}, POINTER_MOVE_EVENT_MANAGER_DEFAULT_CONFIG);
    this.setConfig(config);

    this.position = new Vector2();
    this.velocity = new Vector2();
    this.acceleration = new Vector2();
    this.previousPosition = new Vector2();
    this.previousVelocity = new Vector2();

    this.listen();
  }

  public setConfig(config?: Partial<PointerMoveEventManagerConfig>): this {
    if (typeof config === 'object') {
      Object.assign(this.config, config);
    }

    return this;
  }

  private createMousePointerMoveEvent(
    type: PointerMoveEventType,
    event: MouseEvent,
  ): PointerMoveEvent {
    this.updateVectors(event);

    return {
      event,
      type,
      time: Date.now(),
      position: Vector2.clone(this.position),
      velocity: Vector2.clone(this.velocity),
      acceleration: Vector2.clone(this.acceleration),
    };
  }

  private updateVectors(event: MouseEvent) {
    this.position = this.getVector2FromEvent(event);
    this.velocity = Vector2.subtract(this.position, this.previousPosition);
    this.acceleration = Vector2.subtract(this.velocity, this.previousVelocity);

    this.previousPosition.equals(this.position);
    this.previousVelocity.equals(this.velocity);
  }

  private getVector2FromEvent(event: MouseEvent): Vector2 {
    const { clientX, clientY } = event;

    return new Vector2(clientX, clientY);
  }

  private eventHandlerMouseEnter = (event: MouseEvent) => {
    const pointerEvent = this.createMousePointerMoveEvent('enter', event);

    this.previousPointerEvent = pointerEvent;

    this.isInside = true;

    this.config.onEvent(event, this);
    this.config.onEnter(pointerEvent, this);
  };

  private eventHandlerMouseMove = (event: MouseEvent) => {
    const pointerEvent = this.createMousePointerMoveEvent('move', event);

    this.previousPointerEvent = pointerEvent;
    this.previousPointerMoveEvent = pointerEvent;

    this.isInside = this.checkIfPointerIsInside(event);

    this.config.onEvent(event, this);

    if (this.isMoving === false) {
      this.isMoving = true;

      this.previousPointerMoveStartEvent = pointerEvent;
      
      this.config.onMoveStart(pointerEvent, this);
    }

    this.config.onMove(pointerEvent, this);
  };

  private eventHandlerMouseLeave = (event: MouseEvent) => {
    const pointerEvent = this.createMousePointerMoveEvent('leave', event);
    
    this.previousPointerEvent = pointerEvent;

    this.isInside = false;

    this.config.onEvent(event, this);
    this.config.onLeave(pointerEvent, this);
  };

  private eventHandlerMouseMoveEnd = () => {
    if (this.isMoving === true) {
      this.isMoving = false;

      this.config.onMoveEnd(this.previousPointerMoveEvent as PointerMoveEvent, this);
    }
  };

  private checkIfPointerIsInside(event: MouseEvent) {
    if (this.config.target === window) {
      if (
           event.clientX >= 0
        && event.clientX <= Viewport.width
        && event.clientY >= 0
        && event.clientY <= Viewport.height
      ) {
        return true;
      }
    } else {
      const { top, bottom, left, right } = (this.config.target as HTMLElement).getBoundingClientRect();

      if (
           event.clientY >= top
        && event.clientY <= bottom
        && event.clientX >= left
        && event.clientX <= right
      ) {
        return true;
      }
    }

    return false;
  }

  public listen() {
    const { target, debounceWait } = this.config;

    if (
      typeof target === 'object'
      && (
        target === window
        || DOMUtil.isHTMLElement(target) === true
      )
    ) {
      window.addEventListener('mousemove', this.eventHandlerMouseMove as EventListener);

      this.debouncer = Util.debounce(debounceWait, this.eventHandlerMouseMoveEnd);
      window.addEventListener('mousemove', this.debouncer as EventListener);

      target.addEventListener('mouseenter', this.eventHandlerMouseEnter as EventListener);

      target.addEventListener('mouseleave', this.eventHandlerMouseLeave as EventListener);
    }
  }

  public stopListen() {
    const { target } = this.config;

    if (
      typeof target === 'object'
      && (
        target === window
        || DOMUtil.isHTMLElement(target) === true
      )
    ) {
      window.removeEventListener('mousemove', this.eventHandlerMouseMove as EventListener);

      window.removeEventListener('mousemove', this.debouncer as EventListener);

      target.removeEventListener('mouseenter', this.eventHandlerMouseEnter as EventListener);

      target.removeEventListener('mouseleave', this.eventHandlerMouseLeave as EventListener);
    }
  }
}
